import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Form, Formik, FormikHelpers } from 'formik';
import { Autocomplete, Box, Button, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Checkbox, Text, yup } from '@app/formik';
import { Modal } from '@app/ui/modal';
import { Text as MuiText } from '@app/ui/forms';
import { error as toastError, success as toastSuccess } from '@app/snackbars';

import { COLOR_SCORING_RULE_UPSERT_ONE } from '../../gql';
import { ColorScoringCaseInput, ColorScoringRule, ColorScoringRuleUpsertInput, RuleField, RuleType } from '../../types';
import { COLOR_RULE_TYPE_VALUES } from '../../constants';
import {
  ColorCaseFormValue,
  colorRowError,
  emptyColorCaseRow,
  getGraphQLErrorMessage,
  toColorCaseInput,
  toColorCaseRow,
} from '../../utils';
import { ColorCaseListEditor } from './ColorCaseListEditor';

export interface ColorScoringRuleEditorProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly ruleSetVersionId: number;
  readonly rule: ColorScoringRule | null;
  readonly fields: RuleField[];
  readonly onSaved: () => Promise<unknown> | void;
}

interface FormValues {
  type: RuleType;
  ruleText: string;
  enabled: boolean;
  field: string;
  points: string;
  cases: ColorCaseFormValue[];
}

const buildInitialValues = (rule: ColorScoringRule | null): FormValues => {
  if (!rule) {
    return {
      type: RuleType.exact,
      ruleText: '',
      enabled: true,
      field: '',
      points: '',
      cases: [emptyColorCaseRow()],
    };
  }

  return {
    type: rule.type,
    ruleText: rule.ruleText,
    enabled: rule.enabled,
    field: rule.field ?? '',
    points: rule.points != null ? String(rule.points) : '',
    cases: rule.cases.length ? rule.cases.map(toColorCaseRow) : [emptyColorCaseRow()],
  };
};

// One form for CREATE and EDIT (D1/D2). `exact`: field picker + case list, no `points`. `ai`: prose
// `ruleText` + one signed `points`, no field picker, no cases (AC-1). A Dialog with an explicit
// Save, not `<AutoSave/>`: unlike the full-page watchlist `RuleEditor` (reached by navigation, where
// autosave protects against a lost edit on nav-away), this is opened/closed in place and every
// sibling colour dialog (Publish/Discard/Restore/Delete) already uses the same explicit-confirm
// shape — see the handover for the reasoning.
export const ColorScoringRuleEditor = ({
  open,
  onClose,
  ruleSetVersionId,
  rule,
  fields,
  onSaved,
}: ColorScoringRuleEditorProps) => {
  const { t } = useTranslation('common');
  const translate = (key: string) => t(key) ?? key;

  const fieldsByPath = useMemo(() => new Map(fields.map(field => [field.path, field])), [fields]);

  const [upsertScoringRule, { loading }] = useMutation<{ colorScoringRuleUpsertOne: ColorScoringRule }>(
    COLOR_SCORING_RULE_UPSERT_ONE,
  );

  const initialValues = useMemo(() => buildInitialValues(rule), [rule?.id]);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        ruleText: yup.string().required(),
        field: yup.string().when('type', {
          is: RuleType.exact,
          then: yup.string().required(),
        }),
        points: yup.string().when('type', {
          is: RuleType.ai,
          then: yup
            .string()
            .test(
              'isInteger',
              translate('rules.colorCoding.validation.pointsRequired'),
              value => !!value && Number.isInteger(Number(value)),
            ),
        }),
        cases: yup.array().test({
          name: 'colorCaseRows',
          test(rows, context) {
            const values = context.parent as FormValues;

            if (values.type !== RuleType.exact) {
              return true;
            }

            const caseRows = (rows ?? []) as ColorCaseFormValue[];

            if (!caseRows.length) {
              return values.enabled === false
                ? true
                : context.createError({ message: translate('rules.colorCoding.validation.casesRequired') });
            }

            const field = fieldsByPath.get(values.field);

            return caseRows.every(
              row => !colorRowError(row, field, translate, { requireField: false, requirePoints: true }),
            )
              ? true
              : context.createError({ message: translate('rules.colorCoding.validation.caseInvalid') });
          },
        }),
      }),
    [fieldsByPath, t],
  );

  const handleSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    const isExact = values.type === RuleType.exact;
    const field = fieldsByPath.get(values.field);

    const input: ColorScoringRuleUpsertInput = {
      ...(rule ? { id: rule.id } : {}),
      ruleSetVersionId,
      type: values.type,
      ruleText: values.ruleText.trim(),
      enabled: values.enabled,
      ...(isExact
        ? {
            field: values.field,
            cases: values.cases.map(row => toColorCaseInput(row, field)).filter((c): c is ColorScoringCaseInput => !!c),
          }
        : { points: Number(values.points) }),
    };

    try {
      await upsertScoringRule({ variables: { input } });
      toastSuccess(t('rules.colorCoding.scoringRule.savedMessage'));
      await onSaved();
      onClose();
    } catch (e) {
      toastError(getGraphQLErrorMessage(e) ?? t('general.error.somethingWentWrong'));
      helpers.setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="md"
      disableBackdropClose
      title={
        rule
          ? t('rules.colorCoding.scoringRule.editTitle', { ruleId: rule.ruleId })
          : t('rules.colorCoding.scoringRule.newTitle')
      }
    >
      <Formik
        key={rule?.id ?? 'new'}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, isSubmitting, setFieldValue }) => (
          <Form noValidate>
            {!!rule?.note && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {rule.note}
              </Typography>
            )}

            <Text name="ruleText" label={t('rules.editor.field.ruleText.label')} multiline rows={2} sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2, mb: 2 }}>
              <Box sx={{ width: { xs: '100%', sm: 200 } }}>
                <Text
                  select
                  name="type"
                  label={t('rules.editor.field.type.label')}
                  options={COLOR_RULE_TYPE_VALUES.map(value => ({
                    id: value,
                    name: t(`rules.type.option.${value}`) ?? '',
                  }))}
                />
              </Box>

              <Checkbox name="enabled" label={t('rules.editor.field.enabled.label')} />
            </Box>

            {values.type === RuleType.exact ? (
              <>
                <Autocomplete
                  options={fields}
                  value={fieldsByPath.get(values.field) ?? null}
                  groupBy={option => t(`rules.editor.appliesTo.${option.appliesTo}`) ?? option.appliesTo}
                  getOptionLabel={option => option.label}
                  isOptionEqualToValue={(option, selected) => option.path === selected.path}
                  onChange={(_event, next) => {
                    setFieldValue('field', next?.path ?? '');
                    setFieldValue('cases', [emptyColorCaseRow()]);
                  }}
                  renderInput={params => (
                    <MuiText {...params} label={t('rules.editor.condition.fieldLabel')} sx={{ mb: 2 }} />
                  )}
                />

                <ColorCaseListEditor
                  name="cases"
                  showFieldPicker={false}
                  fixedField={fieldsByPath.get(values.field)}
                  showPoints
                  disabled={!values.field}
                  emptyMessage={t('rules.colorCoding.scoringRule.noCasesYet') ?? ''}
                />
              </>
            ) : (
              <Box sx={{ width: { xs: '100%', sm: 200 } }}>
                <Text
                  name="points"
                  label={t('rules.colorCoding.row.pointsLabel')}
                  helperText={t('rules.colorCoding.scoringRule.aiPointsHelperText')}
                  sx={{ mb: 2 }}
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 3 }}>
              <Button onClick={onClose}>{t('rules.editor.cancelButton')}</Button>
              <LoadingButton loading={isSubmitting || loading} variant="contained" color="primary" type="submit">
                {t('rules.colorCoding.saveButton')}
              </LoadingButton>
            </Box>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
